import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import { Schedule } from '../../../types/working-hours';
import DayScheduleRow from './DayScheduleRow';

interface WorkingHourInput {
  id?: string;
  weekday: number;
  start_min: number;
  end_min: number;
  _destroy?: boolean;
}

interface WeeklyScheduleTabProps {
  schedule?: Schedule;
  isLoading: boolean;
}

const WeeklyScheduleTab: React.FC<WeeklyScheduleTabProps> = ({ schedule, isLoading }) => {
  const queryClient = useQueryClient();
  const [workingHours, setWorkingHours] = useState<Map<number, WorkingHourInput[]>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize working hours from schedule
  useEffect(() => {
    if (schedule) {
      const hoursMap = new Map<number, WorkingHourInput[]>();

      // Initialize all days
      for (let i = 1; i <= 7; i++) {
        hoursMap.set(i, []);
      }

      // Add existing working hours
      schedule.working_hours.forEach(wh => {
        const dayHours = hoursMap.get(wh.weekday) || [];
        dayHours.push({
          id: wh.id,
          weekday: wh.weekday,
          start_min: wh.start_min,
          end_min: wh.end_min,
        });
        hoursMap.set(wh.weekday, dayHours);
      });

      setWorkingHours(hoursMap);
      setHasChanges(false);
    }
  }, [schedule]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!schedule) {
        const allHours: WorkingHourInput[] = [];
        workingHours.forEach((hours) => {
          hours.filter(h => !h._destroy).forEach(h => allHours.push(h));
        });

        return expertDashboardApi.schedules.create({
          name: 'Regular Hours',
          is_default: true,
          working_hours_attributes: allHours.map(h => ({
            weekday: h.weekday,
            start_min: h.start_min,
            end_min: h.end_min,
          })),
        });
      } else {
        const allHours: WorkingHourInput[] = [];
        workingHours.forEach((hours) => {
          hours.forEach(h => allHours.push(h));
        });

        return expertDashboardApi.schedules.update(schedule.id, {
          working_hours_attributes: allHours.map(h => ({
            id: h.id,
            weekday: h.weekday,
            start_min: h.start_min,
            end_min: h.end_min,
            _destroy: h._destroy,
          })),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert-schedules'] });
      toast.success('Schedule saved successfully');
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save schedule');
    },
  });

  const handleDayChange = (weekday: number, hours: WorkingHourInput[]) => {
    const newHours = new Map(workingHours);
    newHours.set(weekday, hours);
    setWorkingHours(newHours);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-12 bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  // Calculate total weekly hours
  const totalMinutes = Array.from(workingHours.values()).reduce((total, dayHours) => {
    return total + dayHours
      .filter(h => !h._destroy)
      .reduce((dayTotal, h) => dayTotal + (h.end_min - h.start_min), 0);
  }, 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  // Count working days
  const workingDaysCount = Array.from(workingHours.entries()).filter(([_, hours]) =>
    hours.some(h => !h._destroy)
  ).length;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Mobile Summary Bar */}
      <div className="flex sm:hidden items-center justify-between bg-purple-50 rounded-xl px-3 py-2">
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5">
            <span className="text-purple-600 font-semibold">{totalHours}h{totalMins > 0 && ` ${totalMins}m`}</span>
            <span className="text-purple-400">/wk</span>
          </div>
          <span className="text-purple-300">&middot;</span>
          <span className="text-purple-600">{workingDaysCount} days</span>
        </div>
        {hasChanges && (
          <span className="text-amber-600 text-xs font-medium">Unsaved</span>
        )}
      </div>

      {/* Schedule Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {[1, 2, 3, 4, 5, 6, 7].map(weekday => (
            <DayScheduleRow
              key={weekday}
              weekday={weekday}
              hours={workingHours.get(weekday) || []}
              onChange={(hours) => handleDayChange(weekday, hours)}
            />
          ))}
        </div>
      </div>

      {/* Save Bar */}
      <div className="flex items-center justify-between gap-4 px-1 sm:px-0">
        <div className="hidden sm:block text-sm text-gray-500">
          {hasChanges ? (
            <span className="text-amber-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              All changes saved
            </span>
          )}
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={!hasChanges || saveMutation.isPending}
          className={`
            w-full sm:w-auto px-6 py-2.5 rounded-xl font-medium transition-all text-sm
            ${hasChanges
              ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm active:scale-[0.98]'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          {saveMutation.isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </span>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
};

export default WeeklyScheduleTab;
